//library
import React, { useCallback, useMemo, useState, useEffect, useRef } from "react";

import api from "services/api";

//services
import getSolutionCategory from "services/get-solution-category";

//common
import formatIndexNumber from "common/format-index-number";
import serveRequestErrors from "common/serve-request-errors";

//constants
import { statusOptions } from "constants/status-options";

//dto
import ErrorResponseDto from "dto/services/error-response-dto";
import { PageEBrochureTableDto } from "dto/pages/e-brochure/page-e-brochure-dto";
import EBrochureTableProps, { EBrochureDataDto, EBrochureUpdateDto } from "dto/components/pages/e-brochure/page-e-brochure-table-dto";

//components
import AppTextarea from "components/app-textarea";
import AppPagination from "components/app-pagination";
import AppSelectInput from "components/app-select-input";

//assets
import arrowUp from "assets/images/components/app-faq-table/arrow-up.svg";
import chevronUp from "assets/images/components/app-faq-table/chevron-up.svg";
import arrowDown from "assets/images/components/app-faq-table/arrow-down.svg";
import editIcon from "assets/images/components/app-articles-table/edit-icon.svg";
import chevronDown from "assets/images/components/app-faq-table/chevron-down.svg";
import wasteBinIcon from "assets/images/components/app-link-input/trash-icon.svg";

const EBrochureTable: React.FC<EBrochureTableProps> = ({ data, setDisableAddNew, newRow, setNewRow, onUpdateData, onCreateData, onHandlePageChange, fetching, paginationTable, id, selectedCategoryId }) => {
	const tableRef = useRef<HTMLDivElement | null>(null);
	const newRowRef = useRef<HTMLTableRowElement | null>(null);
	const [editingRow, setEditingRow] = useState<string | null>();
	const [expandedRows, setExpandedRows] = useState<string[]>([]);
	const [isReadyData, setIsReadyData] = useState<boolean>(fetching);
	const [brochureData, setBrochureData] = useState<EBrochureDataDto>(data);
	const [errors, setErrors] = useState<Record<string, Record<string, string>>>({});
	const [brochureUpdatedData, setBrochureUpdatedData] = useState<EBrochureUpdateDto>();
	const startIndex = paginationTable.currentPage * paginationTable.tableSize;
	const isEmptyData = useMemo(() => brochureData.length === 0 && !fetching, [brochureData.length, fetching]);

	const toggleRow = useCallback((id: string) => {
		setExpandedRows((prev) => (prev.includes(id) ? prev.filter((rowId) => rowId !== id) : [...prev, id]));
	}, []);

	//prettier-ignore
	const onHandleEdit = useCallback((id: string) => {
        setEditingRow(id);
        setDisableAddNew(true);
    }, [setDisableAddNew]);

	//prettier-ignore
	const onHandleInputChange = useCallback((id: string, field: "cover" | "document" | "title" | "category" | "displayStatus", value: string) => {
		const updatedBrochures = brochureUpdatedData ?? brochureData.find((career) => career.id === id);

		if (updatedBrochures) {
			const updatedData = { ...updatedBrochures, [field]: value };

			setBrochureUpdatedData(updatedData);

			setErrors((prev) => ({ ...prev, [id]: { ...prev[id], [field]: value.trim() ? "" : "This field is required" }}));
		}
	}, [brochureData, brochureUpdatedData]);

	//prettier-ignore
	const onHandleNewInputChange = useCallback((field: string, value: string) => {
		setNewRow((prev: any) => ({ ...prev, [field]: value }));

		setErrors((prev) => ({ ...prev, new: { ...prev["new"], [field]: value.trim() ? "" : "This field is required" }}));
	}, [setNewRow]);

	const validateRow = (rowData: any) => {
		const requiredFields = ["cover", "title", "document", "category", "displayStatus"];
		const newErrors: Record<string, string> = {};

		requiredFields.forEach((field) => {
			if (!rowData[field]?.trim()) newErrors[field] = "This field is required";
		});
		return newErrors;
	};

	//prettier-ignore
	const onHandleSave = useCallback((id: string) => {
		if (brochureUpdatedData) {
			const rowErrors = validateRow(brochureUpdatedData);

			if (Object.keys(rowErrors).length > 0) {
				setErrors((prev) => ({ ...prev, [id]: rowErrors }));
				return; 
			}

			const newBrochure = brochureData.map((brochure) => (brochure.id === id ? { ...brochure, ...brochureUpdatedData } : brochure));

			setIsReadyData(false);
			setBrochureData(newBrochure);
			onUpdateData(brochureUpdatedData);
			setEditingRow(null);
			setBrochureUpdatedData(null);
			setDisableAddNew(false);
			setErrors((prev) => ({ ...prev, [id]: {} })); 
		}
	}, [brochureData, brochureUpdatedData, onUpdateData, setDisableAddNew]);

	const updateBrochureData = useCallback((newBrochureData: PageEBrochureTableDto[], updatedBrochure: PageEBrochureTableDto, newSortNumber?: number) => {
		const updatedData = { ...updatedBrochure, sortNumber: newSortNumber ?? updatedBrochure.sortNumber };
		setBrochureUpdatedData(updatedData);
		setBrochureData(newBrochureData);
	}, []);

	//prettier-ignore
	const fetchPageData = useCallback(async (targetPage: number): Promise<PageEBrochureTableDto[]> => {
		try {
			const payload = { size: paginationTable.tableSize, page: targetPage };
			const response = await api.get.brochure.brochures(selectedCategoryId, payload);
			return response?.data?.data?.list?.content || [];
		} catch (error) {
			const err = error as Error | ErrorResponseDto;
			serveRequestErrors(err);
			return [];
		}
	}, [selectedCategoryId, paginationTable.tableSize]);

	//prettier-ignore
	const onHandleMove = useCallback(async (id: string, move: number) => {
		if (selectedCategoryId === "all") return;

		const index = brochureData.findIndex((brochure) => brochure.id === id);
		const updatedBrochure = brochureUpdatedData ?? brochureData[index];
		setEditingRow(id);
		setDisableAddNew(true);

		const isFirstItem = index === 0 && move === -1;
		const isLastItem = index === brochureData.length - 1 && move === 1;
		const moveToPrevPage = isFirstItem && paginationTable.currentPage > 0;
		const moveToNextPage = isLastItem && paginationTable.currentPage < paginationTable.totalPages - 1;

		if (!isFirstItem && !isLastItem) {
			const newBrochure = [...brochureData];
			newBrochure.splice(index, 1);
			const movedDataSortNumber = brochureData[index + move].sortNumber;

			updateBrochureData(newBrochure, updatedBrochure, movedDataSortNumber);
			newBrochure.splice(index + move, 0, updatedBrochure);
		} else if (moveToPrevPage || moveToNextPage) {
			const targetPage = moveToPrevPage ? paginationTable.currentPage - 1 : paginationTable.currentPage + 1;
			const isNextPage = move === 1;
			const pageData = await fetchPageData(targetPage);

			if (pageData.length > 0) {
				const targetItem = isNextPage ? pageData[0] : pageData[pageData.length - 1];
				const newBrochureData = isNextPage ? [updatedBrochure, ...pageData.slice(1)] : [...pageData.slice(0, -1), updatedBrochure];
				
				updateBrochureData(newBrochureData, updatedBrochure, targetItem.sortNumber);
				onHandlePageChange(targetPage);
			}
		}
	}, [selectedCategoryId, brochureData, brochureUpdatedData, setDisableAddNew, paginationTable.currentPage, paginationTable.totalPages, updateBrochureData, fetchPageData, onHandlePageChange]);

	//prettier-ignore
	const onHandleDelete = useCallback((id: string) => {
		const updatedBrochure = brochureData.find((brochure) => brochure.id === id);

		if (updatedBrochure) {
			const changePage = paginationTable.numberOfElements < 2 && paginationTable.currentPage > 0 ? paginationTable.currentPage - 1 : undefined;

			onUpdateData(id, changePage);
		}

		setEditingRow(null);
		setBrochureUpdatedData(null);
		setDisableAddNew(false);
	}, [brochureData, setDisableAddNew, paginationTable.numberOfElements, paginationTable.currentPage, onUpdateData]);

	const truncateText = useCallback((text: string, maxLength: number) => {
		return text.length > maxLength ? `${text.slice(0, maxLength)}...` : text;
	}, []);

	const onHandleCancel = () => {
		setNewRow(null);
		setDisableAddNew(false);
		setErrors({});
	};

	const onHandleSaveNew = () => {
		if (!newRow) return;
		const rowErrors = validateRow(newRow);

		if (Object.keys(rowErrors).length > 0) {
			setErrors((prev) => ({ ...prev, new: rowErrors }));
			return;
		}

		const newFaq = {
			cover: newRow?.cover || "",
			document: newRow?.document || "",
			title: newRow?.title || "",
			category: newRow?.category || "",
			displayStatus: newRow?.displayStatus || "",
			status: "active",
		};

		onCreateData(newFaq);
		setNewRow(null);
		setDisableAddNew(false);
		setErrors((prev) => ({ ...prev, new: {} }));
	};

	const getLabel = (options: { label: string; value: string }[], value: string) => {
		const option = options.find((option) => option.value === value);
		return option ? option.label : value;
	};

	const cancelEditing = useCallback(() => {
		setEditingRow(null);
		setBrochureUpdatedData(null);
		setDisableAddNew(false);
		setErrors((prev) => ({ ...prev, [editingRow || ""]: {} }));
	}, [setDisableAddNew, editingRow]);

	useEffect(() => {
		const handleClickOutside = (event: MouseEvent) => {
			if (editingRow && tableRef.current && !tableRef.current.contains(event.target as Node)) cancelEditing();
		};

		document.addEventListener("mousedown", handleClickOutside);
		return () => document.removeEventListener("mousedown", handleClickOutside);
	}, [editingRow, cancelEditing]);

	useEffect(() => {
		if (!fetching) {
			setIsReadyData(true);
		} else {
			setEditingRow(null);
		}
	}, [fetching]);

	useEffect(() => {
		if (!editingRow && isReadyData) setBrochureData(data);
	}, [data, editingRow, isReadyData]);

	useEffect(() => {
		if (brochureUpdatedData && brochureUpdatedData.id) setEditingRow(brochureUpdatedData.id);
	}, [brochureUpdatedData]);

	useEffect(() => {
		if (newRow && newRowRef.current) newRowRef.current.scrollIntoView({ behavior: "smooth", block: "start" });
	}, [newRow]);

	return (
		<div className="app-brochure-table">
			<div className="brochure-table">
				<div className="brochure-table__wrapper" ref={tableRef}>
					<table className="brochure-table__container">
						<thead className="brochure-table__header">
							<tr>
								<th className="brochure-table__th">No.</th>
								<th className="brochure-table__th">Cover Image URL</th>
								<th className="brochure-table__th">Document URL</th>
								<th className="brochure-table__th">Title</th>
								<th className="brochure-table__th">Category</th>
								<th className="brochure-table__th">Status</th>
							</tr>
						</thead>

						<tbody className="brochure-table__content">
							{brochureData.map((item, index) => {
								const isEditing = editingRow === item.id;
								const isExpanded = expandedRows.includes(item.id);
								const rowErrors = errors[item.id] || {};
								const isAllCategory = selectedCategoryId === "all";

								return (
									<tr className="brochure-table__tr" key={item.id}>
										<td className="brochure-table__td">{formatIndexNumber(index + 1 + startIndex)}</td>

										<td className="brochure-table__td">
											{isEditing ? (
												//prettier-ignore
												<AppTextarea fieldRequired error={rowErrors.cover} touched={!!brochureUpdatedData} name="cover" value={brochureUpdatedData?.cover ?? item.cover} maxRows={100} rows={5} onChange={(e) => onHandleInputChange(item.id, "cover", e.target.value)} tooltipContent="Recommended dimensions: (1080x1920)px or 6:9 ratio" />
											) : isExpanded ? (
												item.cover
											) : (
												truncateText(item.cover ?? "", 30)
											)}
										</td>

										<td className="brochure-table__td">
											{isEditing ? (
												//prettier-ignore
												<AppTextarea fieldRequired error={rowErrors.document} touched={!!brochureUpdatedData} name="document" value={brochureUpdatedData?.document ??item.document} maxRows={100} rows={5} onChange={(e) => onHandleInputChange(item.id, "document", e.target.value)} />
											) : isExpanded ? (
												item.document
											) : (
												truncateText(item.document ?? "", 30)
											)}
										</td>

										<td className="brochure-table__td">
											{isEditing ? (
												//prettier-ignore
												<AppTextarea fieldRequired name="title" error={rowErrors.title} touched={!!brochureUpdatedData} value={brochureUpdatedData?.title ?? item.title} maxCharLength={90} rows={5} maxRows={20} onChange={(e) => onHandleInputChange(item.id, "title", e.target.value)} />
											) : isExpanded ? (
												item.title
											) : (
												truncateText(item.title, 30)
											)}
										</td>

										<td className="brochure-table__td">
											{isEditing ? (
												//prettier-ignore
												<AppSelectInput fieldRequired name="category" error={rowErrors.category} touched={!!brochureUpdatedData} placeholder={item.categoryName} value={brochureUpdatedData?.category ?? item.category} onChange={(e) => onHandleInputChange(item.id, "category", e.target.value)} loadOptions={() => getSolutionCategory(id ?? "")} searchable={false} />
											) : (
												item.categoryName
											)}
										</td>

										<td className="brochure-table__td">
											{isEditing ? (
												//prettier-ignore
												<AppSelectInput fieldRequired name="displayStatus" error={rowErrors.displayStatus} touched={!!brochureUpdatedData} placeholder={item.displayStatus} value={brochureUpdatedData?.displayStatus ?? item.displayStatus} onChange={(e) => onHandleInputChange(item.id, "displayStatus", e.target.value)} options={statusOptions} searchable={false} />
											) : (
												<span className={item.displayStatus === "active" ? "brochure-table__td--active" : "brochure-table__td--inactive"}>{getLabel(statusOptions, item.displayStatus ?? "")}</span>
											)}
										</td>

										<td className="brochure-table__td">
											{isEditing ? (
												<div className="brochure-table__edit">
													<button className="brochure-table__icon-button--bordered" onClick={() => onHandleDelete(item.id)}>
														<img src={wasteBinIcon} alt="delete" />
													</button>

													{!isAllCategory && (
														<>
															<button className="brochure-table__icon-button--bordered" onClick={() => onHandleMove(item.id, -1)}>
																<img src={arrowUp} alt="up" />
															</button>

															<button className="brochure-table__icon-button--bordered" onClick={() => onHandleMove(item.id, 1)}>
																<img src={arrowDown} alt="down" />
															</button>
														</>
													)}

													<button className="brochure-table__icon-button--save" onClick={() => onHandleSave(item.id)}>
														Save
													</button>
												</div>
											) : (
												<div className="brochure-table__actions">
													<button className="brochure-table__icon-button" onClick={() => toggleRow(item.id)}>
														<img src={isExpanded ? chevronUp : chevronDown} alt="" />
													</button>

													<button className="brochure-table__icon-button" onClick={() => onHandleEdit(item.id)}>
														<img src={editIcon} alt="Edit" />
													</button>
												</div>
											)}
										</td>
									</tr>
								);
							})}

							{newRow && (
								<tr className="brochure-table__tr" ref={newRowRef}>
									<td className="brochure-table__td">{formatIndexNumber(paginationTable.totalElements + 1)}</td>

									<td className="brochure-table__td">
										{/* prettier-ignore */}
										<AppTextarea fieldRequired name="new-cover" error={errors["new"]?.cover} touched={!!newRow} value={newRow?.cover || ""} maxRows={100} placeholder="Enter URL" onChange={(e) => onHandleNewInputChange("cover", e.target.value)} tooltipContent="Recommended dimensions: (1080x1920)px or 6:9 ratio" />
									</td>

									<td className="brochure-table__td">
										{/* prettier-ignore */}
										<AppTextarea fieldRequired name="new-document" value={newRow?.document || ""} error={errors["new"]?.document} touched={!!newRow} maxRows={100} placeholder="Enter URL" onChange={(e) => onHandleNewInputChange("document", e.target.value)} />
									</td>

									<td className="brochure-table__td">
										{/* prettier-ignore */}
										<AppTextarea fieldRequired name="new-title" value={newRow?.title || ""} error={errors["new"]?.title} touched={!!newRow} maxCharLength={90} maxRows={20} placeholder="Enter text" onChange={(e) => onHandleNewInputChange("title", e.target.value)} />
									</td>

									<td className="brochure-table__td">
										{/* prettier-ignore */}
										<AppSelectInput fieldRequired name="new-category" error={errors["new"]?.category} touched={!!newRow} placeholder="Please Select" value={newRow?.category || ""} onChange={(e) => onHandleNewInputChange("category", e.target.value)} loadOptions={() => getSolutionCategory(id ?? "")} searchable={false} />
									</td>

									<td className="brochure-table__td">
										{/* prettier-ignore */}
										<AppSelectInput fieldRequired name="displayStatus"  error={errors["new"]?.displayStatus} touched={!!newRow} placeholder="Please Select" value={newRow?.displayStatus || ""} onChange={(e) => onHandleNewInputChange("displayStatus", e.target.value)} options={statusOptions} searchable={false} />
									</td>

									<td className="brochure-table__td">
										<div className="brochure-table__actions">
											<button className="brochure-table__icon-button--cancel" onClick={onHandleCancel}>
												Cancel
											</button>

											<button className="brochure-table__icon-button--add" onClick={onHandleSaveNew}>
												Add
											</button>
										</div>
									</td>
								</tr>
							)}

							{fetching && (
								<tr className="brochure-table__tr">
									<td className="brochure-table__message" colSpan={7}>
										Loading...
									</td>
								</tr>
							)}

							{isEmptyData && (
								<tr className="brochure-table__tr">
									<td className="brochure-table__message" colSpan={7}>
										No records found
									</td>
								</tr>
							)}
						</tbody>
					</table>
				</div>

				{!isEmptyData && !fetching && (
					<AppPagination currentPage={paginationTable.currentPage} totalPages={paginationTable.totalPages} onPageChange={onHandlePageChange} numberOfElements={paginationTable.numberOfElements} />
				)}
			</div>
		</div>
	);
};

export default EBrochureTable;
